import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Tag, TextArea } from '@carbon/react';
import { formatVnd, formatVnDateTime, timeZoneLabel } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import { LineChart } from '../components/LineChart';
import type { Command } from './Commands';

// Truc tiep (P1-D04): nguoi dang o tung game, CCU theo phut, van dang mo va tien cuoc dang giu.
// Du lieu doc thang tu he A / he B qua API; tu tai lai moi 5 giay (CCU moi phut).
type Player = { userId: number; name: string | null; stake: number | null; sockets: number };
type Group = { he: 'A' | 'B'; game: string; name: string; room: string | number | null; players: Player[] };
type Online = { groups: Group[]; heA: boolean; heB: boolean; at: string };
// null = he B did not answer that minute (no combined count)
type CcuPoint = { t: string; players: number | null; heA: number | null; heB: number | null; sockets: number | null };
type Ccu = { points: CcuPoint[]; step: 'minute' | 'hour'; hours: number; peak?: { t: string; players: number } | null; now?: number | null };
type Round = {
  he: string; game: string; name: string; round: string; phase: string; openedAt: string | null; ageSec: number | null;
  stake: number; players: number; refundable: boolean; stuck: boolean; source: 'memory' | 'log'; voiding: boolean;
};
type Held = { count: number; amount: number };
type Rounds = {
  rounds: Round[]; stuckAfterSec: number; heA: boolean; heB: boolean;
  held: { heA: Held; heB: Held | null; onDisk: Held } | null;
  onDisk: { userId: number; amount: number; game: string | null; round: string | null }[] | null;
  pending: { id: string; sourceId: string | null; status: string; createdAt: string; lastError: string | null }[];
};
type KickTarget = { player: Player; group: Group };

const HOURS: [number, string][] = [[1, '1 giờ'], [6, '6 giờ'], [24, '24 giờ'], [72, '3 ngày'], [168, '7 ngày']];
// he B gui ma rieng, ban he A gui ten trang thai cua client (games/lib/ban_chung.js)
const PHASE: Record<string, string> = {
  cuoc: 'Nhận cược', tra_thuong: 'Trả thưởng', chuan_bi: 'Chuẩn bị', huy: 'Đang hủy', bay: 'Đang bay', no: 'Đã nổ', log: 'Không máy chủ nào giữ',
  Start_Table: 'Chuẩn bị', Betting: 'Nhận cược', End_Betting: 'Đóng cược', Show_Result: 'Mở kết quả', Finish: 'Trả thưởng',
};

function age(s: number | null) {
  if (s == null) return '-';
  if (s < 60) return `${s} giây`;
  if (s < 3600) return `${Math.floor(s / 60)} phút ${s % 60} giây`;
  return `${Math.floor(s / 3600)} giờ ${Math.floor((s % 3600) / 60)} phút`;
}
const heLabel = (he: string) => 'Hệ ' + he;
const vnTime = (iso: string) => formatVnDateTime(iso).slice(11, 16);
const vnDayHour = (iso: string) => formatVnDateTime(iso).slice(0, 5) + ' ' + formatVnDateTime(iso).slice(11, 13) + 'h';
const refundError = (c: Command) => {
  const loi = (c.result && typeof c.result.loi === 'string' ? c.result.loi : null) ?? c.lastError;
  return loi ? HE_A_ERRORS[loi] ?? loi : c.status;
};

function PlayerName({ p }: { p: Player }) {
  const { can } = useAuth();
  const label = <>{p.name || 'Người chơi'} <span className="muted num">#{p.userId}</span></>;
  return can('users.view') ? <Link className="cell-link" to={'/users/' + p.userId}>{label}</Link> : <span>{label}</span>;
}

function KickModal({ t, onClose }: { t: KickTarget; onClose: (msg: string | null) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<{ closedHeA: number; closedHeB: number | null }>('/api/admin/live/kick', {
      userId: t.player.userId, game: t.group.game, ...(reason.trim() ? { reason: reason.trim() } : {}),
    }),
    onSuccess: (r) => onClose(`Đã đóng ${r.closedHeA} kết nối hệ A và ${r.closedHeB == null ? 'không rõ số kết nối hệ B (hệ B không trả lời)' : r.closedHeB + ' kết nối hệ B'} của ${t.player.name || '#' + t.player.userId} với ${t.group.name}.`),
  });
  return (
    <Modal open danger modalHeading={`Đuổi ${t.player.name || '#' + t.player.userId} khỏi ${t.group.name}`} primaryButtonText="Đuổi khỏi game" secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={() => onClose(null)} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>Chỉ đóng kết nối của người chơi này với {t.group.name}. Phiên đăng nhập và các game khác vẫn giữ nguyên.</p>
        {t.player.stake ? <p>Người chơi đang có <Money v={t.player.stake} unit /> cược trong game này.</p> : null}
        <TextArea id="kick-reason" labelText="Lý do (không bắt buộc)" rows={2} maxCount={200} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

function RefundModal({ r, onClose }: { r: Round; onClose: (res: { command: Command; existed: boolean } | null) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command; existed: boolean }>('/api/admin/live/rounds/refund', { game: r.game, round: r.round, reason: reason.trim() }),
    onSuccess: (res) => onClose(res),
  });
  const len = reason.trim().length;
  return (
    <Modal open danger modalHeading={`Hoàn cả ván ${r.round} ${r.name}`} primaryButtonText="Hoàn cả ván" secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending || len < 3 || len > 300} onRequestClose={() => onClose(null)} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>
          Trả lại toàn bộ tiền cược thật của ván này cho từng người chơi (<span className="num">{r.players}</span> người, tổng <Money v={r.stake} unit />) và hủy ván:
          ván không có kết quả, không ai được trả thưởng. Không làm lại được.
        </p>
        <TextArea id="refund-reason" labelText="Lý do (bắt buộc, từ 3 ký tự)" rows={2} maxCount={300} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

function RefundResult({ res, onClose }: { res: { command: Command; existed: boolean }; onClose: () => void }) {
  const c = res.command;
  const r = c.result ?? {};
  const [kind, title, subtitle] = res.existed ? ['info', 'Ván này đã có lệnh hoàn', 'Không tạo lệnh mới.'] as const
    : c.status === 'SUCCEEDED' ? ['success', 'Đã hoàn cả ván', typeof r.so_nguoi === 'number' ? `${r.so_nguoi} người, tổng ₩${formatVnd(Number(r.tong_hoan ?? 0))}.` : ''] as const
    : c.status === 'FAILED' ? ['error', 'Hệ game không hoàn ván', refundError(c) + '.'] as const
    : ['warning', 'Lệnh hoàn đang gửi', (c.lastError ? refundError(c) + '. ' : '') + 'CMS tự gửi lại cho tới khi hệ game trả lời.'] as const;
  // the link sits under the notification: an InlineNotification may hold no interactive child and an
  // ActionableNotification traps focus (a click elsewhere on the page scrolls back to it)
  return (
    <div className="result-note">
      <InlineNotification kind={kind} lowContrast onClose={onClose} title={title} subtitle={subtitle} />
      <div className="row"><CommandStatus s={c.status} /><Link className="cell-link" to={'/commands/' + c.id}>Xem lệnh</Link></div>
    </div>
  );
}

function CcuSection() {
  const [hours, setHours] = useState(24);
  const q = useQuery({ queryKey: ['live', 'ccu', hours], queryFn: () => api.get<Ccu>('/api/admin/live/ccu', { hours }), refetchInterval: 60_000 });
  const d = q.data;
  const points = (d?.points ?? []).filter((p) => p.players != null).map((p) => ({
    key: p.t, label: d?.step === 'hour' ? vnDayHour(p.t) : vnTime(p.t),
    value: p.players ?? 0, hint: `${formatVnDateTime(p.t).slice(0, 16)} · hệ A ${p.heA ?? '-'}, hệ B ${p.heB ?? '-'}, ${p.sockets ?? '-'} kết nối`,
  }));
  return (
    <section className="panel section">
      <div className="panel__head">
        <h2>Người online (CCU)</h2>
        {d?.peak ? <span className="muted">Đỉnh <span className="num">{d.peak.players}</span> lúc <When v={d.peak.t} /></span> : null}
      </div>
      <div className="row presets" role="group" aria-label="Khoảng thời gian">
        {HOURS.map(([h, l]) => <Button key={h} kind={h === hours ? 'secondary' : 'ghost'} size="sm" onClick={() => setHours(h)}>{l}</Button>)}
      </div>
      {q.isLoading ? <BlockSkeleton lines={6} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : points.length ? (
        <LineChart points={points} title={`Số người online trong ${hours} giờ qua`} unit=" người" />
      ) : <EmptyState title="Chưa có số liệu CCU" body="Hệ A ghi số người online mỗi phút; số liệu hiện ở đây sau phút đầu tiên." />}
      <p className="muted">{d?.step === 'hour' ? 'Mỗi điểm là đỉnh của một giờ.' : 'Mỗi điểm là một phút.'} Giờ theo {timeZoneLabel()}; người chơi có mặt ở cả hệ A và hệ B chỉ tính một lần.</p>
    </section>
  );
}

function OnlineSection({ q, onKick }: { q: UseQueryResult<Online>; onKick: (t: KickTarget) => void }) {
  const { can } = useAuth();
  if (q.isLoading) return <BlockSkeleton lines={6} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const rows = q.data.groups.flatMap((g) => g.players.map((p) => ({ g, p })));
  if (!rows.length) return <EmptyState title="Không có ai trong game" body="Người chơi hiện ở đây khi vào một game." />;
  return (
    <div className="stack">
      <div className="row">
        {q.data.groups.map((g) => <Tag key={g.he + g.game + String(g.room)} size="sm" type="cool-gray">{g.name}{g.room != null && g.room !== '' ? ' · phòng ' + g.room : ''}: {g.players.length}</Tag>)}
      </div>
      <table className="plain">
        <thead><tr><th>Người chơi</th><th>Game</th><th>Hệ</th><th className="r">Đang cược</th><th className="r">Kết nối</th>{can('live.kick') ? <th /> : null}</tr></thead>
        <tbody>
          {rows.map(({ g, p }) => (
            <tr key={g.he + g.game + String(g.room) + ':' + p.userId}>
              <td className="is-title"><PlayerName p={p} /></td>
              <td data-label="Game"><div>{g.name}{g.room != null && g.room !== '' ? <span className="muted"> · phòng {g.room}</span> : null}</div><div className="muted num">{g.game}</div></td>
              <td data-label="Hệ">{heLabel(g.he)}</td>
              <td className="r" data-label="Đang cược">{p.stake ? <Money v={p.stake} unit /> : <span className="muted">-</span>}</td>
              <td className="r num" data-label="Kết nối">{p.sockets}</td>
              {can('live.kick') ? <td className="r" data-label=""><Button kind="danger--ghost" size="sm" onClick={() => onKick({ player: p, group: g })}>Đuổi khỏi game</Button></td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundsSection({ q, onRefund }: { q: UseQueryResult<Rounds>; onRefund: (r: Round) => void }) {
  const { can } = useAuth();
  if (q.isLoading) return <BlockSkeleton lines={6} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { rounds, stuckAfterSec, pending } = q.data;
  const refunding = new Set(pending.map((p) => p.sourceId));
  return (
    <div className="stack">
      {pending.length ? (
        <ul className="list">
          {pending.map((p) => (
            <li key={p.id}><CommandStatus s={p.status} /><span>Hoàn ván {p.sourceId?.split(':')[1] ?? ''}</span>
              <Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span>
              {p.lastError ? <span className="text-error">{HE_A_ERRORS[p.lastError] ?? p.lastError}</span> : null}</li>
          ))}
        </ul>
      ) : null}
      {rounds.length ? (
        <div className="table-scroll">
          <table className="plain vis">
            <thead><tr><th>Game</th><th>Ván</th><th>Giai đoạn</th><th>Mở lúc</th><th className="r">Đã mở</th><th className="r">Cược thật</th><th className="r">Người</th><th>Trạng thái</th>{can('rounds.refund') ? <th /> : null}</tr></thead>
            <tbody>
              {rounds.map((r) => (
                <tr key={r.he + r.game + r.round}>
                  <td className="is-title"><div className="vis__name">{r.name}</div><div className="muted num">{r.game} · {heLabel(r.he)}</div></td>
                  <td className="num" data-label="Ván">{r.round}</td>
                  <td data-label="Giai đoạn">{PHASE[r.phase] ?? r.phase}</td>
                  <td data-label="Mở lúc"><When v={r.openedAt} /></td>
                  <td className="r num" data-label="Đã mở">{age(r.ageSec)}</td>
                  <td className="r" data-label="Cược thật"><Money v={r.stake} unit /></td>
                  <td className="r num" data-label="Người">{r.players}</td>
                  <td data-label="Trạng thái">
                    <div className="row">
                      {r.stuck ? <Tag size="sm" type="red">Kẹt</Tag> : <Tag size="sm" type="green">Đang chạy</Tag>}
                      {r.source === 'log' ? <Tag size="sm" type="warm-gray">Chỉ trong nhật ký ván</Tag> : null}
                      {r.voiding ? <Tag size="sm" type="purple">Đang hủy</Tag> : null}
                    </div>
                  </td>
                  {can('rounds.refund') ? (
                    <td className="r" data-label="">
                      {r.refundable ? <Button kind="danger--tertiary" size="sm" disabled={refunding.has(r.game + ':' + r.round)} onClick={() => onRefund(r)}>Hoàn cả ván</Button> : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title="Không có ván nào đang mở" body="Ván của game chung ván (Tài Xỉu, Bầu Cua, bàn trực tiếp...) hiện ở đây khi máy chủ đang chạy." />}
      <p className="note">
        Ván mở quá {age(stuckAfterSec)} được đánh dấu kẹt. "Chỉ trong nhật ký ván": nhật ký còn ghi ván mở nhưng không máy chủ nào giữ (máy chủ dừng giữa ván),
        tiền cược của ván đó nằm trong phần chờ hoàn trên đĩa. Hoàn cả ván chỉ làm được khi ván còn nhận cược.
      </p>
    </div>
  );
}

export function LivePage() {
  const qc = useQueryClient();
  const online = useQuery({ queryKey: ['live', 'online'], queryFn: () => api.get<Online>('/api/admin/live/online'), refetchInterval: 5000 });
  const rounds = useQuery({ queryKey: ['live', 'rounds'], queryFn: () => api.get<Rounds>('/api/admin/live/rounds'), refetchInterval: 5000 });
  const [kick, setKick] = useState<KickTarget | null>(null);
  const [kicked, setKicked] = useState<string | null>(null);
  const [refund, setRefund] = useState<Round | null>(null);
  const [refunded, setRefunded] = useState<{ command: Command; existed: boolean } | null>(null);

  const o = online.data;
  const rd = rounds.data;
  const players = o ? new Set(o.groups.flatMap((g) => g.players.map((p) => p.userId))).size : null;
  // false = that game server did not answer this call
  const down = (['A', 'B'] as const).filter((he) => o?.[`he${he}`] === false || rd?.[`he${he}`] === false);
  const held = rd?.held;
  const stuck = rd?.rounds.filter((r) => r.stuck).length ?? 0;

  return (
    <div className="page">
      <PageHeader title="Trực tiếp"
        lead="Ai đang ở game nào, số người online theo thời gian và các ván đang mở cùng tiền cược máy chủ đang giữ. Tự cập nhật mỗi 5 giây." />
      {down.length ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title={down.map(heLabel).join(' và ') + ' không trả lời'}
          subtitle="Danh sách người chơi và ván đang mở thiếu phần của máy chủ đó. Kiểm tra trang Vận hành và sao lưu." />
      ) : null}
      {kicked ? <InlineNotification kind="success" lowContrast onClose={() => setKicked(null)} title="Đã đuổi khỏi game" subtitle={kicked} /> : null}
      {refunded ? <RefundResult res={refunded} onClose={() => setRefunded(null)} /> : null}

      <div className="kpis kpis--live">
        <div className="kpi kpi--lead">
          <span className="kpi__label">Đang trong game</span>
          <span className="kpi__value">{players ?? '-'}</span>
          <span className="kpi__hint">{o ? `${o.groups.length} game / phòng có người` : online.error ? 'không đọc được' : ''}</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Ván đang mở</span>
          <span className="kpi__value">{rd ? rd.rounds.length : '-'}</span>
          <span className="kpi__hint">{stuck ? <span className="text-error">{stuck} ván kẹt</span> : 'không ván nào kẹt'}</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Cược hệ A đang giữ</span>
          <span className="kpi__value">{held ? <Money v={held.heA.amount} unit /> : '-'}</span>
          <span className="kpi__hint">{held ? `${held.heA.count} khoản` : 'không đọc được'}</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Cược hệ B đang giữ</span>
          <span className="kpi__value">{held?.heB ? <Money v={held.heB.amount} unit /> : '-'}</span>
          <span className="kpi__hint">{held?.heB ? `${held.heB.count} khoản` : held ? 'hệ B không trả lời' : 'không đọc được'}</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Chờ hoàn trên đĩa</span>
          <span className="kpi__value">{held ? <Money v={held.onDisk.amount} unit /> : '-'}</span>
          <span className="kpi__hint">{held ? `${held.onDisk.count} khoản, trả khi máy chủ mở lại` : 'không đọc được'}</span>
        </div>
      </div>

      <CcuSection />

      <section className="section">
        <h2>Người đang chơi</h2>
        <OnlineSection q={online} onKick={setKick} />
      </section>

      <section className="section">
        <h2>Ván đang mở</h2>
        <RoundsSection q={rounds} onRefund={setRefund} />
      </section>

      {rd?.onDisk?.length ? (
        <section className="section">
          <h2>Tiền cược chờ hoàn trên đĩa</h2>
          <table className="plain">
            <thead><tr><th>Người chơi</th><th>Game</th><th>Ván</th><th className="r">Số tiền</th></tr></thead>
            <tbody>
              {rd.onDisk.map((k, i) => (
                <tr key={i}>
                  <td className="is-title"><PlayerName p={{ userId: k.userId, name: null, stake: null, sockets: 0 }} /></td>
                  <td data-label="Game">{k.game ?? '-'}</td>
                  <td className="num" data-label="Ván">{k.round ?? '-'}</td>
                  <td className="r" data-label="Số tiền"><Money v={k.amount} unit /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {kick ? <KickModal t={kick} onClose={(msg) => { setKick(null); if (msg) { setKicked(msg); qc.invalidateQueries({ queryKey: ['live', 'online'] }); } }} /> : null}
      {refund ? (
        <RefundModal r={refund} onClose={(res) => { setRefund(null); if (res) { setRefunded(res); qc.invalidateQueries({ queryKey: ['live', 'rounds'] }); } }} />
      ) : null}
    </div>
  );
}
